<script setup lang="ts">
import { onMounted, ref } from 'vue';
import {
    Dialog,
    DialogTrigger,
    DialogScrollContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { storeOrdersApiClient } from '@api-client/store_orders';
import type { ResType } from '@api-client/typeUtils';
import { toast } from '@/ui-plus/sonner';
import { formatCurrency } from '@lib/utils/formatCurrency';
import { Loader2 } from 'lucide-vue-next';
import CustomerCreditHistoryView from '@/orders-manager/customers/CustomerCreditHistoryView.vue';
import { History } from 'lucide-vue-next';

const creditsApiPath = storeOrdersApiClient.customers.credits[':storeId'].$post;
type CreditBalance = ResType<typeof creditsApiPath>["data"]["balances"][0];

const props = defineProps<{
    storeId: string;
    userId: string;
}>();

const isBlocked = ref<boolean | null>(null);
const isBlockLoading = ref(false);
const isBlockSubmitting = ref(false);
const blockError = ref('');

const balances = ref<CreditBalance[]>([]);
const isCreditsLoading = ref(false);
const creditsError = ref('');
const amounts = ref<Record<string, string>>({});
const submittingCurrency = ref<string | null>(null);
const creditErrors = ref<Record<string, string>>({});

const loadBlockStatus = async () => {
    isBlockLoading.value = true;
    blockError.value = '';
    try {
        const res = await (await storeOrdersApiClient.customers.block_status[':storeId'].$post({
            param: { storeId: props.storeId },
            json: { userId: props.userId },
        })).json();
        if (res.error) {
            blockError.value = res.error.message || 'Failed to load block status';
            toast.error(blockError.value);
            return;
        }
        isBlocked.value = res.data.isBlocked;
    } catch (e) {
        console.error(e);
        blockError.value = 'Failed to load block status';
        toast.error(blockError.value);
    } finally {
        isBlockLoading.value = false;
    }
};

const loadBalances = async () => {
    isCreditsLoading.value = true;
    creditsError.value = '';
    try {
        const res = await (await storeOrdersApiClient.customers.credits[':storeId'].$post({
            param: { storeId: props.storeId },
            json: { userId: props.userId },
        })).json();
        if (res.error) {
            creditsError.value = res.error.message || 'Failed to load credits';
            toast.error(creditsError.value);
            return;
        }
        balances.value = res.data.balances || [];
        amounts.value = Object.fromEntries(
            balances.value.map((item) => [item.currency, ''])
        );
        creditErrors.value = {};
    } catch (e) {
        console.error(e);
        creditsError.value = 'Failed to load credits';
        toast.error(creditsError.value);
    } finally {
        isCreditsLoading.value = false;
    }
};

const toggleBlock = async () => {
    if (isBlocked.value === null) return;
    isBlockSubmitting.value = true;
    try {
        const api = isBlocked.value
            ? storeOrdersApiClient.customers.unblock[':storeId'].$post
            : storeOrdersApiClient.customers.block[':storeId'].$post;
        const res = await (await api({
            param: { storeId: props.storeId },
            json: { userId: props.userId },
        })).json();
        if (res.error) {
            toast.error(res.error.message || 'Failed to update customer block status');
            return;
        }
        isBlocked.value = res.data.isBlocked;
        toast.success(res.data.isBlocked ? 'Customer blocked' : 'Customer unblocked');
    } catch (e) {
        console.error(e);
        toast.error('Failed to update customer block status');
    } finally {
        isBlockSubmitting.value = false;
    }
};

const submitCredit = async (action: 'ADD' | 'REMOVE', currency: string) => {
    const value = Number(amounts.value[currency]);
    creditErrors.value = { ...creditErrors.value, [currency]: '' };
    if (!Number.isFinite(value) || value <= 0) {
        creditErrors.value = { ...creditErrors.value, [currency]: 'Enter an amount greater than 0' };
        return;
    }
    submittingCurrency.value = currency;
    try {
        const res = await (await storeOrdersApiClient.customers.modify_credit[':storeId'].$post({
            param: { storeId: props.storeId },
            json: {
                userId: props.userId,
                amount: value,
                action,
                currency,
            },
        })).json();
        if (res.error) {
            creditErrors.value = { ...creditErrors.value, [currency]: res.error.message || 'Failed to update credit' };
            toast.error(res.error.message || 'Failed to update credit');
            return;
        }
        toast.success(action === 'ADD' ? 'Credit added' : 'Credit removed');
        amounts.value = { ...amounts.value, [currency]: '' };
        await loadBalances();
    } catch (e) {
        console.error(e);
        creditErrors.value = { ...creditErrors.value, [currency]: 'An error occurred while updating credit' };
        toast.error('An error occurred while updating credit');
    } finally {
        submittingCurrency.value = null;
    }
};

onMounted(() => {
    loadBlockStatus();
    loadBalances();
});
</script>

<template>
    <div class="space-y-6 mt-4">
        <div class="p-3 border rounded-lg space-y-2">
            <div class="flex items-center justify-between gap-2">
                <div class="font-medium">Block status</div>
                <Badge v-if="isBlocked"
                       variant="destructive">
                    Blocked
                </Badge>
            </div>
            <div v-if="isBlockLoading"
                 class="flex items-center py-2 text-sm text-muted-foreground">
                <Loader2 class="h-4 w-4 animate-spin mr-2" />
                Loading...
            </div>
            <div v-else-if="blockError"
                 class="text-destructive text-sm">
                {{ blockError }}
            </div>
            <Button v-else-if="isBlocked !== null"
                    class="w-full"
                    :variant="isBlocked ? 'outline' : 'destructive'"
                    :disabled="isBlockSubmitting"
                    @click="toggleBlock">
                <Loader2 v-if="isBlockSubmitting"
                         class="h-4 w-4 animate-spin mr-2" />
                {{ isBlocked ? 'Unblock customer' : 'Block customer' }}
            </Button>
        </div>

        <div class="space-y-3">
            <div class="font-medium">Credits</div>
            <div v-if="isCreditsLoading && balances.length === 0"
                 class="flex justify-center items-center py-8">
                <Loader2 class="h-6 w-6 animate-spin" />
                <span class="ml-2">Loading credits...</span>
            </div>
            <div v-else-if="creditsError"
                 class="text-destructive text-sm">
                {{ creditsError }}
            </div>
            <div v-else
                 class="space-y-3">
                <div v-for="item in balances"
                     :key="item.currency"
                     class="p-3 border rounded-lg space-y-2">
                    <div class="font-semibold">
                        {{ formatCurrency(item.balance, item.currency) }}
                    </div>
                    <div class="flex flex-wrap gap-2">
                        <Input v-model="amounts[item.currency]"
                               type="number"
                               min="0"
                               step="0.01"
                               placeholder="Amount"
                               class="w-28" />
                        <Button type="button"
                                size="sm"
                                :disabled="submittingCurrency !== null"
                                @click="submitCredit('ADD', item.currency)">
                            Add
                        </Button>
                        <Button type="button"
                                size="sm"
                                variant="destructive"
                                :disabled="submittingCurrency !== null"
                                @click="submitCredit('REMOVE', item.currency)">
                            Remove
                        </Button>
                    </div>
                    <div v-if="creditErrors[item.currency]"
                         class="text-destructive text-sm">
                        {{ creditErrors[item.currency] }}
                    </div>
                </div>
            </div>

            <Dialog>
                <DialogTrigger as-child>
                    <Button size="sm"
                            variant="outline">
                        <History class="h-4 w-4 mr-1" />
                        History
                    </Button>
                </DialogTrigger>
                <DialogScrollContent class="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle class="flex items-center gap-2">
                            <History class="h-5 w-5" />
                            Credit history
                        </DialogTitle>
                    </DialogHeader>
                    <CustomerCreditHistoryView :store-id="storeId"
                                               :user-id="userId" />
                </DialogScrollContent>
            </Dialog>

        </div>
    </div>
</template>
